// fcmService.js - push notifications: device tokens and messages that arrive while the app is open.
//
// Three kinds of device, one list of tokens per person (fcmTokens/<uid>.devices), so a phone and a
// laptop are both notified (the old single `token` field kept only the device that signed in last):
//   • web / installed PWA — Firebase Messaging in the service worker (public/sw.js)
//   • Android / iOS app   — the Capacitor Push Notifications plugin, reached through the native
//                           bridge (window.Capacitor.Plugins). The web build replaces the
//                           @capacitor/push-notifications package with a stub that always says
//                           "denied" (vite.config.js), which is why the apps never registered.
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getApp } from 'firebase/app';
import { doc, setDoc } from 'firebase/firestore';
import { getFirebase } from '@services/firebase';
import { Capacitor } from '@capacitor/core';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const ANDROID_CHANNEL = 'pcake';

let messagingInstance = null;
const getMessagingInstance = () => {
  if (!messagingInstance) messagingInstance = getMessaging(getApp());
  return messagingInstance;
};

/** The native push plugin, when this page runs inside the Android / iOS app. */
const nativePush = () => {
  try {
    const cap = window.Capacitor;
    if (!cap?.isNativePlatform?.()) return null;
    return cap.Plugins?.PushNotifications || null;
  } catch { return null; }
};
const nativePlatform = () => {
  try { return window.Capacitor?.getPlatform?.() || Capacitor.getPlatform(); } catch { return 'web'; }
};
export const isNativeApp = () => !!nativePush();

const deviceKey = (token) => String(token).replace(/[^A-Za-z0-9]/g, '').slice(-60);

const saveTokenToFirestore = async (token, userId, userRole, platform) => {
  const { db } = getFirebase();
  const updatedAt = new Date().toISOString();
  await setDoc(doc(db, 'fcmTokens', userId), {
    token,                        // latest device — read by anything still using the old field
    role: userRole || 'User',
    platform,
    updatedAt,
    devices: { [deviceKey(token)]: { token, platform, updatedAt } },
  }, { merge: true });
};

let nativeListenersAdded = false;

/**
 * Register for push and save this device's token. Native app: always (the app asks the OS for
 * permission itself). Web: only once the browser has granted notification permission.
 */
export const registerFCMToken = async (userId, userRole) => {
  try {
    const PN = nativePush();
    if (PN) {
      const platform = nativePlatform();
      let perm = await PN.checkPermissions();
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await PN.requestPermissions();
      if (perm.receive !== 'granted') return null;

      if (platform === 'android') {
        await PN.createChannel({
          id: ANDROID_CHANNEL, name: 'PCake', description: 'Chat, enquiry and order updates',
          importance: 5, visibility: 1, sound: 'default', vibration: true, lights: true,
        }).catch(() => undefined);
      }

      const token = await new Promise((resolve) => {
        let done = false;
        const finish = (t) => { if (!done) { done = true; resolve(t); } };
        PN.addListener('registration', async (tokenData) => {
          const t = tokenData?.value;
          if (t) await saveTokenToFirestore(t, userId, userRole, platform).catch(() => undefined);
          finish(t || null);
        });
        PN.addListener('registrationError', () => finish(null));
        setTimeout(() => finish(null), 15000);
        PN.register();
      });

      if (!nativeListenersAdded) {
        nativeListenersAdded = true;
        // Tapping a notification opens the page it is about (chat, enquiry…).
        PN.addListener('pushNotificationActionPerformed', (action) => {
          const url = action?.notification?.data?.url;
          if (url && typeof url === 'string' && url.startsWith('/')) window.location.assign(url);
        });
      }
      return token;
    }

    // Web / PWA — Firebase Messaging via the service worker
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return null;
    if (Notification.permission !== 'granted') return null;

    const registration = await navigator.serviceWorker.ready;
    const token = await getToken(getMessagingInstance(), { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    if (token) await saveTokenToFirestore(token, userId, userRole, 'web');
    return token;
  } catch (error) {
    console.error('🔔 FCM token registration failed:', error);
    return null;
  }
};

/**
 * Messages that arrive while the app is open. Native: the OS shows them on iOS (see
 * capacitor.config.json presentationOptions); on Android the app is already showing the live
 * chat / enquiry, so nothing more is needed. Web: shown through the service worker.
 */
export const setupForegroundMessages = () => {
  try {
    if (nativePush() || Capacitor.isNativePlatform()) return () => {};

    return onMessage(getMessagingInstance(), (payload) => {
      const title = payload.data?.title || payload.notification?.title || 'PCake';
      const body = payload.data?.body || payload.notification?.body || 'You have new messages';
      const url = payload.data?.url || '/';
      const tag = payload.data?.tag || 'chat-notification';

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(title, {
            body,
            icon: '/pcakelogo-192.png',
            badge: '/pcakelogo.png',
            tag,
            renotify: true,
            data: { url },
          });
        });
      }
    });
  } catch (error) {
    console.error('FCM foreground setup failed:', error);
    return null;
  }
};
